import { z } from 'zod';
import { codeSchema } from '../codeConfig.js';

/**
 * Схемы Question для API контракта (Веха 3).
 *
 * Источник правды:
 *   - CANON §2.B (анонимность): response НИКОГДА не содержит senderFingerprint
 *     и не возвращает «понял, кому ушёл вопрос» (никаких подробностей про
 *     fingerprint/блок — CANON B2/SP4).
 *   - CANON §2.D (zod-strict): все .strict() — unknown поля отвергаются.
 *   - threat-models/api.md → T/I (max-length, no fingerprint leak).
 *
 * Лимиты длины: text.max(500) — комфорт NGL-стиля + storage; boxToken
 * соответствует генерируемому 22-символьному base64url (16 байт = 22 chars
 * без padding) — берём min(8) / max(64) как разумную защитную полосу.
 */
export const CreateQuestionBody = z
  .object({
    boxToken: z.string().min(8).max(64),
    text: z.string().min(1).max(500),
  })
  .strict();
export type CreateQuestionBody = z.infer<typeof CreateQuestionBody>;

/**
 * Response для `POST /questions` / `POST /questions/by-code`. Никаких
 * подробностей про блок/модерацию — только `ok: true` + `questionId`.
 *
 * Зачем `literal(true)` вместо `boolean`: чтобы future-self не залогировал
 * `{ ok: false, reason: 'blocked' }` — CANON B2 запрещает сигнализировать
 * отправителю про блок.
 *
 * `questionId` (security/threat-models/answer-view.md, ingress 2 + I.b):
 * ключ доступа спрашивающего к `GET /questions/:id/answer`. Возвращается
 * ВСЕГДА — и на реально созданный вопрос, и на тихий drop (block/moderation-
 * block), где сервер подставляет опаковый throwaway-id вместо `return` без
 * значения (см. `lib/opaqueQuestionId.ts`). Anti-oracle: клиент не может
 * отличить «доставлено» от «дропнуто» по shape ответа.
 *
 * `.min(20).max(40)` — НАМЕРЕННО loose string, НЕ `.cuid()`: throwaway-id
 * мимикрирует под форму cuid, но не обязан проходить строгий cuid-regex.
 */
export const CreateQuestionResponse = z
  .object({
    ok: z.literal(true),
    questionId: z.string().min(20).max(40),
  })
  .strict();
export type CreateQuestionResponse = z.infer<typeof CreateQuestionResponse>;

/**
 * Тело `POST /questions/by-code` — отправка вопроса по короткому коду (work-019).
 *
 * `code` — валидируется `codeSchema` (единая точка: длина + алфавит из CODE_CONFIG).
 * `text` — те же ограничения, что и в `CreateQuestionBody` (min1/max500).
 * Сервер резолвит code→box внутри, без token в теле (CANON B2, token не утекает).
 */
export const CreateQuestionByCodeBody = z
  .object({
    code: codeSchema,
    text: z.string().min(1).max(500),
  })
  .strict();
export type CreateQuestionByCodeBody = z.infer<typeof CreateQuestionByCodeBody>;

/**
 * Response для `GET /questions/:id/answer` (security/threat-models/answer-view.md).
 *
 * Новый trust boundary: читатель — АНОНИМНЫЙ СПРАШИВАЮЩИЙ, не владелец бокса.
 * Авторизация = обладание непредсказуемым `question.id` (cuid), НЕ ownership.
 *
 * Shape строго ограничен (S.a — не должен утечь `questionText`/`boxId`/
 * `ownerId`/`senderFingerprint`): только `answered` + опциональный `answer`
 * с `text`/`cardImageUrl`/`createdAt`. `{ answered: false, answer: null }`
 * возвращается ЕДИНООБРАЗНО для несуществующего id, pending/hidden/removed
 * вопроса, moderationFlag='blocked' и «нет answer-записи» (T.b anti-oracle —
 * не должен быть existence-oracle).
 */
export const QuestionAnswerView = z
  .object({
    answered: z.boolean(),
    answer: z
      .object({
        text: z.string().nullable(),
        cardImageUrl: z.string().nullable(),
        createdAt: z.string(),
      })
      .nullable(),
  })
  .strict();
export type QuestionAnswerView = z.infer<typeof QuestionAnswerView>;
